import { useEffect, useRef, useState, memo } from 'react';
import '../Dashboard.scss';
import * as echarts from 'echarts';
import IconButton from '@mui/material/IconButton';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/Fullscreen';
import {
  calculateConfidenceInterval,
  COMPACT_FONT_SIZE,
  downloadCSV,
  downloadIcon,
} from './utils';
import { FormControlLabel } from '@mui/material';
import Switch from '@mui/material/Switch';

const ChartStandardDistribution = memo(
  ({
    type,
    emissions,
    minLogRateGlobal,
    maxLogRateGlobal,
    tabType = 'equip',
    logAxis = false,
    compact = false,
    ci,
  }: {
    type: string;
    emissions: any;
    minLogRateGlobal: number;
    maxLogRateGlobal: number;
    tabType: string;
    logAxis?: boolean;
    compact?: boolean;
    ci?: boolean;
  }) => {
    const chartRef = useRef<HTMLDivElement | null>(null);
    const chartInstance = useRef<echarts.ECharts | null>(null);
    const [isFullscreen, setIsFullscreen] = useState(false);

    const [useLogScale, setUseLogScale] = useState(true);

    useEffect(() => {
      if (!chartRef.current) return;

      chartInstance.current = echarts.init(chartRef.current);

      return () => {
        chartInstance.current?.dispose();
      };
    }, []);

    const updateChartOptions = () => {
      if (!chartInstance.current || !emissions || emissions.length === 0)
        return;

      const emissionRates = emissions.map(
        (e: { [x: string]: any }) => e['emission_rate_kgh'] || e['emissions'],
      );

      const startLogValue = Math.log10(minLogRateGlobal);
      const endLogValue = Math.log10(maxLogRateGlobal);
      const numBins = 50;
      const binStep = (endLogValue - startLogValue) / numBins;

      const frequencyBinMap = {} as { [p: string | number]: number };

      let currentLogValue = startLogValue;
      const bins: { start: number; end: number }[] = [];
      while (currentLogValue < endLogValue) {
        const binStart = Math.pow(10, currentLogValue);
        const binEnd = Math.pow(10, currentLogValue + binStep);
        bins.push({ start: binStart, end: binEnd });
        currentLogValue += binStep;
      }

      emissionRates.forEach((value: number) => {
        bins.forEach((bin, index) => {
          if (value >= bin.start && value < bin.end) {
            frequencyBinMap[index] = (frequencyBinMap[index] || 0) + 1;
          }
        });
      });

      const xBinValues = bins.map((bin) =>
        Math.pow(10, (Math.log10(bin.start) + Math.log10(bin.end)) / 2),
      );
      const yBinValues = bins.map((_, index) => frequencyBinMap[index] || 0);

      const { meanValue, lowerBound, upperBound } =
        calculateConfidenceInterval(emissionRates);

      const option = {
        title: {
          text: type,
          left: 'center',
          top: '0%',
          ...(compact
            ? {
                textStyle: {
                  fontSize: COMPACT_FONT_SIZE.toString() + 'px',
                },
              }
            : {}),
        },
        grid: {
          top: compact ? 35 : 60,
          bottom: compact ? 40 : 40,
          left: compact ? 40 : 50,
          right: compact ? 20 : 30,
        },
        xAxis: {
          type: useLogScale ? 'log' : 'value',
          min: minLogRateGlobal,
          max: maxLogRateGlobal,
          name: 'Emission Rate (kg/h)',
          nameLocation: 'middle',
          nameGap: 25,
          axisLabel: {
            formatter: function (value: any) {
              return Number(value) < 1
                ? Number(value).toFixed(1)
                : Number(value).toFixed(0);
            },
          },
          splitNumber: 10,
          interval: 200,
        },
        yAxis: {
          type: 'value',
          name: '',
        },
        series: [
          {
            type: 'bar',
            name: 'Frequency',
            data: xBinValues.map((x, index) => [x, yBinValues[index]]),
            barWidth: 10,
            markLine: {
              symbol: ['none', 'none'],
              data: [
                {
                  xAxis: meanValue,
                  label: {
                    formatter: `Mean: ${meanValue.toFixed(1)}`,
                    position: 'insideEndTop',
                  },
                  lineStyle: {
                    color: 'red',
                    width: 2,
                    type: 'dashed',
                  },
                },
                ...(ci
                  ? [
                      {
                        xAxis: lowerBound,
                        label: {
                          formatter: `Lower CI: ${lowerBound.toFixed(1)}`,
                          position: 'insideEndTop',
                        },
                        lineStyle: {
                          color: 'blue',
                          type: 'dashed',
                        },
                      },
                    ]
                  : []),
                ...(ci
                  ? [
                      {
                        xAxis: upperBound,
                        label: {
                          formatter: `Upper CI: ${upperBound.toFixed(1)}`,
                          position: 'insideEndBottom',
                        },
                        lineStyle: {
                          color: 'blue',
                          type: 'dashed',
                        },
                      },
                    ]
                  : []),
              ],
            },
          },
        ],

        tooltip: {
          trigger: 'axis',
          formatter: function (params: any) {
            const data = params[0].data;
            const xValue = Number(data[0]).toFixed(1);
            const yValue = data[1];
            return `Emission Rate: ${xValue}<br>Frequency: ${yValue}`;
          },
        },
        toolbox: {
          show: isFullscreen,
          right: '2%',
          top: '6%',
          feature: {
            saveAsImage: {},
            myDownload: {
              show: true,
              title: 'Download data',
              icon: downloadIcon,
              onclick: function () {
                downloadCSV([xBinValues, yBinValues]);
              },
            },
            dataZoom: [
              {
                type: 'slider',
              },
              {
                type: 'inside',
              },
            ],
          },
        },
        animation: false,
      };

      chartInstance.current.setOption(option);
    };

    useEffect(() => {
      updateChartOptions();

      if (chartInstance.current) {
        chartInstance.current.resize();
      }
    }, [
      type,
      emissions,
      minLogRateGlobal,
      maxLogRateGlobal,
      tabType,
      compact,
      isFullscreen,
      useLogScale,
    ]);

    const toggleFullscreen = () => {
      setIsFullscreen(!isFullscreen);
    };

    const handleLogScaleToggle = () => {
      setUseLogScale(!useLogScale);
    };

    return (
      <div className={`chart-container ${isFullscreen ? 'fullscreen' : ''}`}>
        <IconButton
          onClick={toggleFullscreen}
          style={{
            position: 'absolute',
            top: '0px',
            left: '10px',
            zIndex: 1000,
          }}
        >
          {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
        </IconButton>
        {logAxis && (
          <FormControlLabel
            control={
              <Switch
                checked={useLogScale}
                onChange={handleLogScaleToggle}
                color="primary"
              />
            }
            label="Log Scale"
            style={{
              position: 'absolute',
              top: '1px',
              right: '0',
              zIndex: 1000,
            }}
          />
        )}
        <div ref={chartRef} className={'chart'} />
      </div>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.type === nextProps.type &&
      prevProps.tabType === nextProps.tabType &&
      prevProps.minLogRateGlobal === nextProps.minLogRateGlobal &&
      prevProps.maxLogRateGlobal === nextProps.maxLogRateGlobal &&
      JSON.stringify(prevProps.emissions) ===
        JSON.stringify(nextProps.emissions)
    );
  },
);

export default ChartStandardDistribution;
